import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import PostAttachments from 'components/modals/addPostModal/PostAttachments';
import { DOCUMENT_MIME_WITH_EXTENSIONS_TYPES } from 'components/utils/constants';
import {
  ArticleSourceType,
  ArticleType,
  AttachmentType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { useFormContext, useWatch } from 'react-hook-form';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { AnswerContainer } from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/PromptHistory';
import RenderLLMMarkdown from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/RenderLLMMarkdown';
import WriteWithAISkeleton from '../WriteWithAI/WriteWithAISkeleton';
import RenderParsedDrafts from './RenderParsedDrafts';
import RenderThumbnails from '@buildwise/pages/companyFiles/components/RenderThumbnails';

type ParseFileFormProps = {
  article: ArticleType | undefined;
  articleType?: ArticleSourceType;
  isFullScreen?: boolean;
  loadingFiles: boolean;
  onDrop: (acceptedFiles: File[]) => void;
  response: any;
  downloadedFiles: AttachmentType[];
  setDownloadedFiles: Dispatch<SetStateAction<AttachmentType[]>>;
};

function ParseFileForm({
  article,
  articleType,
  isFullScreen,
  loadingFiles,
  onDrop,
  response,
  downloadedFiles,
  setDownloadedFiles,
}: ParseFileFormProps) {
  const { resetField } = useFormContext<PostFormData>();
  const { control } = useFormContext<PostFormData>();
  const attachments = useWatch({
    control,
    name: 'attachments',
    defaultValue: [],
  });

  useEffect(() => {
    resetField('attachments');
  }, []);

  return (
    <Box minHeight={600}>
      {response?.genAIResponse || response?.loading || response?.error ? (
        <>
          <RenderThumbnails files={attachments} disabled />
          <AnswerContainer
            width="100% !important"
            padding="16px !important"
            sx={{ borderRadius: '8px', mr: 0 }}
          >
            {response?.loading ? (
              <WriteWithAISkeleton />
            ) : response?.isStreaming ? (
              <RenderLLMMarkdown llmResponse={response?.genAIResponse || ''} />
            ) : response?.error ? (
              <Typography>{response?.error}</Typography>
            ) : (
              <RenderParsedDrafts
                response={response}
                downloadedFiles={downloadedFiles}
                setDownloadedFiles={setDownloadedFiles}
              />
            )}
          </AnswerContainer>
        </>
      ) : (
        <>
          <Note textAlign="center">Let BuildWise AI create a post summarizing your file</Note>
          <PostAttachments
            isFullScreen={isFullScreen}
            articleType={articleType}
            article={article}
            onDrop={onDrop}
            loadingFiles={loadingFiles}
            accept={DOCUMENT_MIME_WITH_EXTENSIONS_TYPES}
            multiple={false}
          />
        </>
      )}
    </Box>
  );
}

const Note = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.tuna,
  fontSize: 16,
  marginBottom: 24,
}));

export default ParseFileForm;
